import { useState, useCallback } from 'preact/hooks';
import type { I18nStrings, LeaveMessageRequest } from '@bitio-builder/shared';

interface InlineContactPromptProps {
  strings: I18nStrings;
  onSubmit: (data: LeaveMessageRequest) => Promise<boolean>;
}

const containerStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignSelf: 'flex-start',
  maxWidth: '85%',
};

const messageStyle: Record<string, string | number> = {
  padding: '8px 16px',
  borderRadius: '16px',
  borderBottomLeftRadius: '4px',
  fontSize: '14px',
  lineHeight: 1.5,
  background: 'white',
  color: 'var(--bb-foreground, #1A1C17)',
  border: '1px solid var(--bb-border, #E5E7EB)',
};

const formContainerStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
  background: 'var(--bb-muted-bg, #F9FAFB)',
  borderRadius: '12px',
  border: '1px solid var(--bb-border, #E5E7EB)',
};

const formRowStyle: Record<string, string | number> = {
  display: 'flex',
  gap: '8px',
};

const inputStyle: Record<string, string | number> = {
  flex: 1,
  padding: '8px 12px',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  fontFamily: 'inherit',
  minWidth: 0,
};

const inputErrorStyle: Record<string, string | number> = {
  ...inputStyle,
  borderColor: 'var(--bb-error, #DC2626)',
};

const buttonStyle: Record<string, string | number> = {
  padding: '8px 16px',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};

const successStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px',
  background: 'var(--bb-muted-bg, #F9FAFB)',
  borderRadius: '12px',
  border: '1px solid var(--bb-border, #E5E7EB)',
  color: 'var(--bb-success, #059669)',
  fontSize: '14px',
};

const errorTextStyle: Record<string, string | number> = {
  fontSize: '11px',
  color: 'var(--bb-error, #DC2626)',
  marginTop: '-4px',
};

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export function InlineContactPrompt({ strings, onSubmit }: InlineContactPromptProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const s = strings.leaveMessage;

  const validate = useCallback((): boolean => {
    const newErrors: { name?: string; email?: string } = {};

    if (!name.trim()) {
      newErrors.name = s.validation.nameRequired;
    }

    if (!email.trim()) {
      newErrors.email = s.validation.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = s.validation.emailInvalid;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, email, s]);

  const handleSubmit = useCallback(
    async (e: Event) => {
      e.preventDefault();

      if (!validate()) return;

      setIsSubmitting(true);

      try {
        const success = await onSubmit({
          name: name.trim(),
          email: email.trim(),
          message: '(kontakt via chatt)',
          metadata: {
            referrer: window.location.href,
            userAgent: navigator.userAgent,
          },
        });

        if (success) {
          setIsSuccess(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [name, email, validate, onSubmit]
  );

  if (isSuccess) {
    return (
      <div style={containerStyle}>
        <div style={messageStyle}>{s.noReplyPrompt}</div>
        <div style={successStyle}>
          <CheckIcon />
          <span>{s.success}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={messageStyle}>{s.noReplyPrompt}</div>
      <form style={formContainerStyle} onSubmit={handleSubmit}>
        <div style={formRowStyle}>
          <input
            type="text"
            value={name}
            onInput={(e) => {
              setName((e.target as HTMLInputElement).value);
              setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            style={errors.name ? inputErrorStyle : inputStyle}
            placeholder={s.name}
            disabled={isSubmitting}
          />
          <input
            type="email"
            value={email}
            onInput={(e) => {
              setEmail((e.target as HTMLInputElement).value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            style={errors.email ? inputErrorStyle : inputStyle}
            placeholder={s.email}
            disabled={isSubmitting}
          />
          <button
            type="submit"
            style={{ ...buttonStyle, opacity: isSubmitting ? 0.7 : 1 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? '...' : strings.send}
          </button>
        </div>
        {(errors.name || errors.email) && (
          <div style={errorTextStyle}>
            {errors.name || errors.email}
          </div>
        )}
      </form>
    </div>
  );
}
